import { TippyMenu } from 'app/components';
import { i18n } from 'app/utils/i18n';
import { useState, useContext, useRef, useEffect } from 'react';
import { useParams, useHistory } from 'react-router';
import { v4 as uuidv4 } from 'uuid';
import { useSaveTemplate, useDeleteTemplate, useCopyTemplate } from 'app/api/template-queries';
import { UserContext } from 'app/state/contexts';
import { Modal } from 'app/components';
import authStore from 'app/utils/authStore';
import { Level } from '../Level';
import { usePermissions } from 'app/utils/hooks/document-permission';

const useIsVisible = (rootMargin = '200px') => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [rootMargin]);

  return [ref, isVisible];
};
const StructureBlock = ({ node, depth = 0 }) => {
  if (!node || !node.children) return null;

  const layouts = node.children.filter((c) => c.type === 'layout');
  const isRow = node.className?.includes('row') || node.className?.includes('horizontal');

  if (layouts.length === 0) {
    return <div style={{ flex: '1 1 0', minHeight: depth <= 1 ? 40 : 20, background: '#e9ecf1', borderRadius: 3 }} />;
  }

  return (
    <div style={{ display: 'flex', flexDirection: isRow ? 'row' : 'column', flex: '1 1 0', gap: isRow ? 5 : 3 }}>
      {layouts.map((child, i) => (
        <StructureBlock key={i} node={child} depth={depth + 1} />
      ))}
    </div>
  );
};

const StructureDiagram = ({ structure, aspectRatio = '210 / 297' }) => {
  const rows = structure?.children?.filter((c) => c.type === 'layout') || [];

  return (
    <div
      style={{
        height: '200px',
        aspectRatio,
        background: '#fff',
        border: '1px solid #e1e5eb',
        borderRadius: '6px 6px 0 0',
        padding: 10,
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        overflow: 'hidden',
      }}
    >
      {/* Header area */}
      <div style={{ height: 14, background: '#e9ecf1', borderRadius: 3, flexShrink: 0 }} />
      {/* Content area */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
        {rows.length > 0 ? (
          rows.map((row, i) => <StructureBlock key={i} node={row} depth={0} />)
        ) : (
          <div style={{ flex: 1, background: '#e9ecf1', borderRadius: 3 }} />
        )}
      </div>
      {/* Footer area */}
      <div style={{ height: 10, background: '#e9ecf1', borderRadius: 3, flexShrink: 0 }} />
    </div>
  );
};

const DiagramPlaceholder = ({ aspectRatio = '210 / 297' }) => (
  <div
    style={{
      height: PREVIEW_HEIGHT,
      aspectRatio,
      background: '#f8f9fa',
      border: '1px solid #e1e5eb',
      borderRadius: '6px 6px 0 0',
    }}
  />
);

const PREVIEW_HEIGHT = 240;

const ThumbnailImage = ({ thumbnail, aspectRatio = '210 / 297' }) => (
  <img
    src={`data:image/png;base64,${thumbnail}`}
    alt=""
    style={{
      height: PREVIEW_HEIGHT,
      aspectRatio,
      objectFit: 'fill',
      padding: 10,
      border: '1px solid #e1e5eb',
      borderRadius: '6px 6px 0 0',
    }}
  />
);

const PreviewImage = ({ organization, template, aspectRatio }) => {
  const [ref, isVisible] = useIsVisible();

  const renderPreview = () => {
    if (!isVisible) return <DiagramPlaceholder aspectRatio={aspectRatio} />;
    if (template.thumbnail) return <ThumbnailImage thumbnail={template.thumbnail} aspectRatio={aspectRatio} />;
    return <StructureDiagram structure={template.structure} aspectRatio={aspectRatio} />;
  };

  return (
    <div className="position-relative" ref={ref} style={{ display: 'flex', justifyContent: 'center' }}>
      {renderPreview()}
      <div className="position-absolute bottom-0 end-0 me-2 mb-1">
        <Level organization={organization} />
      </div>
    </div>
  );
};

const RenameModal = ({ onClose, template }) => {
  const [name, setName] = useState(template?.name);

  const { mutate: saveTemplate } = useSaveTemplate();

  const handleChange = (e) => {
    setName(e.target.value);
  };

  const handleSave = (e) => {
    const newTemplate = {
      ...template,
      name,
      revisionId: uuidv4(),
    };

    saveTemplate(newTemplate);
    onClose();
  };

  return (
    <Modal show={true} onClose={onClose}>
      <label className="form-label" htmlFor={`input-name-${template?.superId}`}>
        {i18n('theme.structure.name')}
      </label>
      <input className="form-control" id={`input-name-${template?.superId}`} value={name} onChange={handleChange} />
      <div className="d-flex justify-content-end mt-5">
        <button type="button" className="btn" onClick={onClose}>
          {i18n('common.button.cancel')}
        </button>
        <button type="button" className="btn btn-primary" onClick={handleSave}>
          {i18n('common.button.save')}
        </button>
      </div>
    </Modal>
  );
};

export const StructurePreview = ({ template, onClick, active, onCopy, aspectRatio }) => {
  const hasGlobalTemplatePermission = authStore.hasGlobalPermission('TEMPLATE_CREATE');

  const history = useHistory();
  const [templateToRename, setTemplateToRename] = useState(null);
  const { documentId } = useParams();
  const { documentPermissions } = usePermissions(documentId);
  const { mutate: saveTemplate } = useSaveTemplate();
  const { selectedOrganization } = useContext(UserContext);
  const { mutate: deleteTemplate } = useDeleteTemplate();
  const { mutate: copyTemplate } = useCopyTemplate();

  const handleMakeGlobal = (template) => {
    const newTemplate = {
      ...template,
      revisionId: uuidv4(),
      documentSuperId: null,
      organization: null,
    };

    saveTemplate(newTemplate);
  };

  const handleMakeOrganization = (template) => {
    const newTemplate = {
      ...template,
      revisionId: uuidv4(),
      documentSuperId: null,
      organization: selectedOrganization,
    };
    saveTemplate(newTemplate);
  };

  const handleDelete = (template) => {
    deleteTemplate(template);
  };

  const handleCopy = (template) => {
    copyTemplate(
      { ...template, organization: selectedOrganization },
      {
        onSuccess: (copiedTemplate) => onCopy(copiedTemplate),
      }
    );
  };

  const handleCloseRenameModal = () => {
    setTemplateToRename(null);
  };

  const buildMenu = (template) => {
    const items = [];
    if ((template.organization && documentPermissions.includes('CODER')) || hasGlobalTemplatePermission) {
      items.push({
        text: i18n('templates.dropdown.edit-name'),
        icon: 'fa-pen',
        onClick: () => setTemplateToRename(template),
      });
    }
    if ((template.organization && documentPermissions.includes('CODER')) || hasGlobalTemplatePermission) {
      items.push({
        text: i18n('templates.dropdown.edit'),
        icon: 'fa-pen',
        onClick: () => history.push(`/documents/${documentId}/templates/${template.superId}`),
      });
    }

    if (template.organization && hasGlobalTemplatePermission) {
      items.push({
        text: i18n('templates.dropdown.make-global-system'),
        icon: 'fa-cloud',
        onClick: () => handleMakeGlobal(template),
      });
    } else if (hasGlobalTemplatePermission) {
      items.push({
        text: i18n('templates.dropdown.make-non-global'),
        icon: 'fa-cloud-slash',
        onClick: () => handleMakeOrganization(template),
      });
    }
    if (template.organization && (hasGlobalTemplatePermission || documentPermissions.includes('CODER'))) {
      items.push({
        text: i18n('templates.dropdown.delete-template'),
        icon: 'fa-trash',
        onClick: () => handleDelete(template),
      });
    }
    if (documentPermissions.includes('CODER') || hasGlobalTemplatePermission) {
      items.push({
        text: i18n('templates.dropdown.copy'),
        icon: 'fa-copy',
        onClick: () => handleCopy(template),
      });
    }
    return items;
  };
  return (
    <>
      <div onClick={onClick} className={`d-flex flex-column align-items-stretch border bg-white rounded ${active ? 'border-blue-800' : ''}`}>
        <button type="button" disabled={onClick ? false : true} className="border-0 p-0 bg-white rounded">
          <PreviewImage organization={template.organization} template={template} aspectRatio={aspectRatio} />
        </button>
        <div
          className={`px-1 text-black border-top text-left rounded-bottom d-flex justify-content-between align-items-center ${
            active ? 'border-blue-800 bg-blue-400' : 'bg-white'
          }`}
        >
          <div className="px-2 py-2">{template.name}</div>
          {(documentPermissions.includes('CODER') || documentPermissions.includes('DESIGN')) && buildMenu(template).length > 0 && (
            <TippyMenu offset={[100, -50]} menu={buildMenu(template)} />
          )}
        </div>
      </div>
      {templateToRename ? <RenameModal template={templateToRename} onClose={handleCloseRenameModal} /> : null}
    </>
  );
};
